import {
  createContext,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
} from 'react';
import { aiThemeForDate } from './ai-theme.helpers';
import { AI_THEMES, type AiTheme } from './ai-theme.model';

const AI_THEME_STORAGE_KEY = 'nodon:ai-theme';
/** The season or holiday of today. */
export const AUTO_AI_THEME_ID = 'auto';
const CHANGED = 'nodon:ai-theme-changed';

/** The last pick, when storage refused it, so it still applies until reload. */
let unstoredId: string | null = null;

const isThemeId = (value: string | null): value is string =>
  value === AUTO_AI_THEME_ID || AI_THEMES.some((row) => row.id === value);

/** The theme picked in the admin menu, or auto when none or an unknown one is stored. */
export const readStoredAiThemeId = (): string => {
  if (typeof window === 'undefined') return AUTO_AI_THEME_ID;
  if (unstoredId !== null) return unstoredId;
  try {
    const raw = window.localStorage.getItem(AI_THEME_STORAGE_KEY);
    return isThemeId(raw) ? raw : AUTO_AI_THEME_ID;
  } catch {
    return AUTO_AI_THEME_ID;
  }
};

/** Store the pick for this browser and redraw every helper. */
export const writeStoredAiThemeId = (id: string): void => {
  if (!isThemeId(id)) return;
  try {
    window.localStorage.setItem(AI_THEME_STORAGE_KEY, id);
    unstoredId = null;
  } catch {
    // Storage blocked: the pick still applies until reload.
    unstoredId = id;
  }
  window.dispatchEvent(new Event(CHANGED));
};

const subscribe = (onChange: () => void) => {
  window.addEventListener(CHANGED, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(CHANGED, onChange);
    window.removeEventListener('storage', onChange);
  };
};

const msUntilNextMidnight = () => {
  const now = new Date();
  const midnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1,
  );
  return Math.max(midnight.getTime() - now.getTime(), 1);
};

/** Today's season or holiday, moving on at each local midnight. */
const useThemeOfToday = (): AiTheme => {
  const [today, setToday] = useState(() => aiThemeForDate(new Date()));
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      timer = setTimeout(() => {
        setToday(aiThemeForDate(new Date()));
        schedule();
      }, msUntilNextMidnight());
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);
  return today;
};

/** Overrides the stored theme below it, for previews. */
export const AiThemeContext = createContext<AiTheme | null>(null);

/**
 * The theme every AI character wears: a preview from {@link AiThemeContext},
 * else the one picked in the admin menu, else today's season or holiday.
 */
export const useAiTheme = (): AiTheme => {
  const preview = useContext(AiThemeContext);
  const today = useThemeOfToday();
  const id = useSyncExternalStore(
    subscribe,
    readStoredAiThemeId,
    () => AUTO_AI_THEME_ID,
  );
  return preview ?? AI_THEMES.find((row) => row.id === id) ?? today;
};
